import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Loader, Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Table, TableColumn } from "@/components/Table/Table";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { ensureHttpProtocol } from "@/utils/url";

import { useLazyGetTestPersonaCategoryResultsQuery } from "@/pages/Private/redux/score/score.api";

import {
	TestPersonaCategoryResults,
	TestPersonaCategoryResultsResponse,
} from "../../schema/score.schema";

interface PersonaTestModalProps {
	onClose: () => void;
}

const PAGE_SIZE = 5;

/**
 * "Test personas" (carried over from the old page, PREC-4055): classifies five
 * people from already-categorized companies against the saved personas.
 */
export const PersonaTestModal: FunctionComponent<PersonaTestModalProps> = ({ onClose }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`leadScoring.${key}`);
	const [page, setPage] = useState(1);
	const [result, setResult] = useState<TestPersonaCategoryResultsResponse | null>(null);
	const [getResults, { isFetching }] = useLazyGetTestPersonaCategoryResultsQuery();

	useEffect(() => {
		getResults({ page, limit: PAGE_SIZE })
			.unwrap()
			.then(setResult)
			.catch(() => setResult(null));
	}, [page, getResults]);

	const columns: TableColumn[] = useMemo(
		() => [
			{
				title: ts("name"),
				uid: "name",
				width: "220px",
				render: (row: TestPersonaCategoryResults) => (
					<div className="flex max-w-[220px] flex-col">
						<div className="flex items-center gap-1 text-sm font-bold text-text">
							<span className="truncate">{row.prospect?.name}</span>
							{row.prospect?.linkedinUrl && (
								<a
									href={ensureHttpProtocol(row.prospect.linkedinUrl)}
									rel="noreferrer"
									target="_blank"
								>
									<LinkedinIcon status="checked" />
								</a>
							)}
						</div>
						<div className="text-xs font-bold text-text">
							{row.prospect?.jobTitleToSeniority?.jobTitle || row.prospect?.title}
						</div>
						{row.prospect?.country && (
							<div className="text-xs text-text-meta">
								{ts("basedIn")}: {row.prospect.country}
							</div>
						)}
						<div className="truncate text-xs text-text-meta">
							{row.prospect?.organization?.name}
						</div>
					</div>
				),
			},
			{
				title: ts("categorizedAs"),
				uid: "category",
				width: "200px",
				render: (row: TestPersonaCategoryResults) => (
					<div className="flex flex-col">
						<div className="text-sm font-bold text-text">
							{row.result || ts("companyWasNotCategorized")}
						</div>
						<div className="text-xs text-text-meta">
							{row.scoreCompanyCategory?.type}
							{row.scoreCompanyCategory?.category ? ` / ${row.scoreCompanyCategory.category}` : ""}
						</div>
					</div>
				),
			},
			{
				title: ts("reasoning"),
				uid: "reason",
				width: "380px",
				render: (row: TestPersonaCategoryResults) => (
					<Tooltip content={row.reason}>
						<div className="line-clamp-3 text-sm text-text-meta">{row.reason}</div>
					</Tooltip>
				),
			},
		],
		[t]
	);

	const rows = result?.data ?? [];

	return (
		<Modal
			isOpened
			handleClose={onClose}
			isLoading={isFetching}
			size="lg"
			subtitle={ts("testPersonas")}
			title={ts("personas.testTitle")}
		>
			<div className="flex w-full flex-col">
				{isFetching && (
					<div className="flex w-full items-center justify-center gap-2 py-6">
						<Loader />
						{ts("mightTakeAMinute")}
					</div>
				)}
				{!isFetching && rows.length === 0 && (
					<p className="text-sm font-semibold text-text" data-testid="persona-test-empty">
						{ts("personas.noResults")}
					</p>
				)}
				{!isFetching && rows.length > 0 && (
					<>
						<Table hideFooter hidePagination columns={columns} data={rows} isLoading={false} />
						<div className="mt-3 flex gap-3">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								disabled={page === 1}
								size={ButtonSize.S}
								title={ts("prevProspects")}
								onClick={() => setPage((current) => current - 1)}
							/>
							<Button
								color={ButtonColor.ACTION}
								disabled={!result?.meta?.hasNextPage}
								size={ButtonSize.S}
								title={ts("nextProspects")}
								onClick={() => setPage((current) => current + 1)}
							/>
						</div>
					</>
				)}
			</div>
		</Modal>
	);
};
